"use client";

import * as React from "react";
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogPortal,
} from "@baluu/ui/components/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import { Button } from "@baluu/ui/components/button";

const BUNDLE_MEMBERSHIP_VIDEO_ID = "KX2TydE8oKM";

type BundleMembershipHelpPopupProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function BundleMembershipHelpPopup({
  open,
  onOpenChange,
}: BundleMembershipHelpPopupProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-[9998] bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          className="fixed top-[50%] left-[50%] z-[9999] w-full max-w-xl translate-x-[-50%] translate-y-[-50%] bg-background rounded-lg border shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <DialogPrimitive.Close className="absolute top-4 right-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none z-10">
            <XIcon className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>

          <div className="flex max-h-[85vh] flex-col gap-4 p-6">
            <DialogHeader>
              <DialogTitle>
                How to redeem your bundles &amp; memberships
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-3 overflow-y-auto pr-1 text-sm text-foreground">
              <p>
                If you own an active bundle or membership, you can easily
                redeem your credits at checkout by following these steps:
              </p>
              <ol className="list-decimal space-y-2 pl-5">
                <li>
                  <span className="font-semibold">Choose your sessions:</span>
                  {" "}select the classes or events you want to attend. Please
                  ignore any pricing or payment requests you see along the way.
                </li>
                <li>
                  <span className="font-semibold">Log in at checkout:</span>
                  {" "}once you reach the final checkout screen, you will be
                  prompted to log in to your account.
                </li>
                <li>
                  <span className="font-semibold">Apply your credits:</span>
                  {" "}this is not automatic. Before completing your booking,
                  make sure to select the option to apply your bundle or
                  membership credits to your total.
                </li>
              </ol>

              <div className="relative w-full overflow-hidden rounded-md bg-black pb-[56.25%]">
                <iframe
                  className="absolute inset-0 h-full w-full border-0"
                  src={`https://www.youtube.com/embed/${BUNDLE_MEMBERSHIP_VIDEO_ID}`}
                  title="Redeeming a bundle or membership"
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button type="button" onClick={() => onOpenChange(false)}>
                Got it
              </Button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

export { BundleMembershipHelpPopup };
export type { BundleMembershipHelpPopupProps };
